// =============================================================================
// DRIP CAMPAIGN WORKER
// Automatically sends re-engagement emails to leads who drop out of funnel
// =============================================================================

import { logger, maskEmail } from '@/utils/logger'
import { supabase } from '../lib/supabase'
import { DRIP_CAMPAIGN, DRIP_ELIGIBLE_STATUSES, DRIP_STOP_STATUSES, COMPANY } from '../config/dripCampaign'
import { DRIP_TEMPLATES } from './dripEmailTemplates'
import { initEmailService, isEmailConfigured } from '../utils/emailService'
import { blissProposalShortLink } from '../utils/blissShortLinks'

const log = logger.module('DripWorker')

// Type definitions
interface LeadData {
  id: string
  email?: string
  firstName?: string
  name?: string
  phone?: string
  status: string
  projectType?: string
  quoteAmount?: number
  createdAt: string
  lastActivityAt: string
  dripState?: Record<string, unknown>
  calendlyUrl?: string
  [key: string]: unknown
}

interface ProcessResult {
  processed: number
  sent: number
  errors: Array<Record<string, unknown>>
}

// EmailJS configuration
const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID

// Worker state
let dripInterval: NodeJS.Timeout | null = null
let isRunning: boolean = false
let lastRunTime: Date | null = null

// =============================================================================
// MAIN WORKER
// =============================================================================

/**
 * Start the drip campaign worker
 * Runs every hour to check for leads needing emails
 */
export function startDripWorker(): void {
  if (isRunning) {
    log.warn('Drip worker already running')
    return
  }

  isRunning = true
  log.info('Drip campaign worker started')

  // Run immediately on start
  processDripCampaign()

  // Then run every hour
  dripInterval = setInterval(() => {
    processDripCampaign()
  }, 60 * 60 * 1000) // Every hour
}

/**
 * Stop the drip worker
 */
export function stopDripWorker() {
  if (dripInterval) {
    clearInterval(dripInterval)
    dripInterval = null
  }
  isRunning = false
  log.info('Drip campaign worker stopped')
}

/**
 * Force run drip processing (for testing)
 */
export function forceProcessDrip() {
  log.info('Force processing drip campaign')
  return processDripCampaign()
}

// =============================================================================
// DRIP PROCESSING LOGIC
// =============================================================================

/**
 * Main processing function - check all leads and send appropriate emails
 */
async function processDripCampaign() {
  lastRunTime = new Date()
  log.debug(`Processing drip campaign at ${lastRunTime.toISOString()}`)

  try {
    // Ensure EmailJS is ready
    await initEmailService()

    if (!isEmailConfigured()) {
      log.warn('EmailJS not configured - drip emails disabled')
      return { processed: 0, sent: 0, errors: [] }
    }

    // Get all leads eligible for drip campaign
    const eligibleLeads = await getEligibleLeads()
    log.debug(`Found ${eligibleLeads.length} leads eligible for drip`)

    let sent = 0
    const errors = []

    for (const lead of eligibleLeads) {
      try {
        const result = await processLeadDrip(lead)
        if (result.sent) {
          sent++
          // Business event: track drip email sent
          log.info(`Drip email #${result.emailNumber} sent to ${maskEmail(lead.email)}`)
        }
      } catch (error) {
        log.error(`Error processing drip for ${maskEmail(lead.email)}:`, error)
        errors.push({ leadId: lead.id, email: maskEmail(lead.email), error: error.message })
      }
    }

    // Business event: summary of drip run
    log.info(`Drip processing complete: ${sent} emails sent, ${errors.length} errors`)
    return { processed: eligibleLeads.length, sent, errors }

  } catch (error) {
    log.error('❌ Drip campaign processing error:', error)
    return { processed: 0, sent: 0, errors: [error.message] }
  }
}

/**
 * Get all leads eligible for drip campaign
 */
async function getEligibleLeads() {
  // First try quote_requests table (main leads source)
  // Note: client data is stored in 'contact_info' column, not 'client'
  const { data: quoteLeads, error: quoteError } = await supabase
    .from('quote_requests')
    .select('*')
    .in('status', DRIP_ELIGIBLE_STATUSES)
    .not('contact_info->>email', 'is', null)
    .order('created_at', { ascending: true })

  if (quoteError) {
    log.error('Error fetching quote_requests for drip:', quoteError)
  }

  // Transform quote_requests to lead format
  // contact_info contains: { name, email, phone, ... }
  const leads = (quoteLeads || []).map(q => ({
    id: q.id,
    source: 'quote_requests',
    email: q.contact_info?.email,
    firstName: q.contact_info?.name?.split(' ')[0] || 'there',
    name: q.contact_info?.name,
    phone: q.contact_info?.phone,
    status: q.status,
    projectType: q.config,
    quoteAmount: q.pricing?.total || q.accuratePricing?.total,
    createdAt: q.created_at,
    lastActivityAt: q.last_updated || q.calendly_link_sent_at || q.last_call_at || q.created_at,
    dripState: q.drip_state || {},
    calendlyUrl: blissProposalShortLink(q.id, { target: 'design' }),
  }))

  return leads.filter(lead => {
    // Must have email
    if (!lead.email) return false

    // Check if enough time has passed since last activity
    const lastActivity = new Date(lead.lastActivityAt)
    const hoursSinceActivity = (Date.now() - lastActivity.getTime()) / (1000 * 60 * 60)

    // Must be at least triggerAfterHours since last activity
    if (hoursSinceActivity < DRIP_CAMPAIGN.triggerAfterHours) {
      return false
    }

    // Check if drip is complete or stopped
    if (lead.dripState?.completed || lead.dripState?.unsubscribed) {
      return false
    }

    return true
  })
}

/**
 * Process drip for a single lead
 */
async function processLeadDrip(lead) {
  log.debug(`Processing lead for drip, status: ${lead.status}`)
  
  const dripState = lead.dripState || {}
  const emailsSent = dripState.emailsSent || []
  const lastEmailSentAt = dripState.lastEmailSentAt ? new Date(dripState.lastEmailSentAt) : null

  log.debug(`Lead has ${emailsSent.length} emails already sent`)

  // Determine which email to send next
  const nextEmail = getNextDripEmail(lead, emailsSent, lastEmailSentAt)

  if (!nextEmail) {
    log.debug('No email due yet for lead')
    return { sent: false, reason: 'not_due' }
  }

  log.debug(`Next email to send: ${nextEmail.id}`)

  // Get email template
  const templateFn = DRIP_TEMPLATES[nextEmail.templateKey]
  if (!templateFn) {
    log.error(`📧 [DripWorker] Missing template: ${nextEmail.templateKey}`)
    return { sent: false, reason: 'missing_template' }
  }

  // Generate email content
  const emailContent = templateFn(lead)
  log.debug(`Generated email with subject: ${emailContent.subject}`)

  // Send email via EmailJS
  const sendResult = await sendDripEmail(lead, emailContent, nextEmail)

  if (sendResult.success) {
    log.debug('Drip email sent successfully')
    // Update drip state in database
    await updateDripState(lead, nextEmail.id, emailsSent)
    return { sent: true, emailNumber: emailsSent.length + 1, emailId: nextEmail.id }
  } else {
    // Only log as error for real failures, not configuration issues
    if (sendResult.error?.includes('not configured')) {
      log.debug(`Drip skipped for ${maskEmail(lead.email)}: ${sendResult.error}`)
    } else {
      log.error(`Drip send failed for ${maskEmail(lead.email)}: ${sendResult.error}`)
    }
    return { sent: false, reason: 'send_failed', error: sendResult.error }
  }
}

/**
 * Determine which email to send next based on timing
 */
function getNextDripEmail(lead, emailsSent, lastEmailSentAt) {
  const now = new Date()
  
  // SPECIAL HANDLING FOR RETARGET LEADS: Send first email immediately
  // Retargeted leads should get their first email right away, not wait days
  if (lead.status === 'retarget' && emailsSent.length === 0) {
    log.debug('Retarget lead - sending first email immediately')
    return DRIP_CAMPAIGN.emails[0]
  }
  
  const dripStartDate = new Date(lead.lastActivityAt)
  dripStartDate.setHours(dripStartDate.getHours() + DRIP_CAMPAIGN.triggerAfterHours)

  const daysSinceDripStart = (now.getTime() - dripStartDate.getTime()) / (1000 * 60 * 60 * 24)

  // Find the next email that:
  // 1. Hasn't been sent yet
  // 2. Is due based on dayOffset
  for (const email of DRIP_CAMPAIGN.emails) {
    // Skip if already sent
    if (emailsSent.includes(email.id)) {
      continue
    }

    // Check if this email is due
    if (daysSinceDripStart >= email.dayOffset) {
      // If there was a previous email, ensure at least 2 days have passed
      if (lastEmailSentAt) {
        const daysSinceLastEmail = (now.getTime() - new Date(lastEmailSentAt).getTime()) / (1000 * 60 * 60 * 24)
        if (daysSinceLastEmail < 2) {
          return null // Too soon after last email
        }
      }
      return email
    }
  }

  return null // No email due yet
}

/**
 * Send drip email via EmailJS
 */
async function sendDripEmail(lead, emailContent, emailConfig) {
  log.debug('Attempting to send drip email')
  
  // Check if EmailJS service ID is configured FIRST
  if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID) {
    log.debug('EmailJS not configured (missing service_id or template_id) - skipping drip email')
    return { success: false, error: 'EmailJS not configured' }
  }
  
  // Check if EmailJS is loaded
  if (typeof window === 'undefined') {
    log.debug('Window not available (server-side?) - skipping drip email')
    return { success: false, error: 'Window not available' }
  }
  
  if (!window.emailjs) {
    // Try to init EmailJS
    try {
      await initEmailService()
      log.debug('EmailJS initialized')
    } catch (e) {
      log.debug('Failed to init EmailJS - skipping drip email')
      return { success: false, error: 'EmailJS not loaded and failed to init' }
    }
  }
  
  if (!window.emailjs) {
    log.debug('EmailJS still not available after init - skipping drip email')
    return { success: false, error: 'EmailJS not loaded' }
  }
  
  if (!isEmailConfigured()) {
    log.debug('EmailJS not configured (missing env vars) - skipping drip email')
    return { success: false, error: 'EmailJS not configured' }
  }

  try {
    // BCC to both personal and company emails
    const bccEmails = 'jasontibesar@gmail.com, sales@blissbunkbeds.com'
    
    const templateParams = {
      to_email: lead.email,
      to_name: lead.name || lead.firstName,
      from_name: COMPANY.name,
      reply_to: COMPANY.email,
      bcc_email: bccEmails, // BCC to both personal and company
      subject: emailContent.subject,
      html_content: emailContent.html,
      text_content: emailContent.text
    }

    const response = await window.emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    )

    // Business event: email successfully sent via EmailJS
    log.info(`Drip email "${emailConfig.id}" sent to ${maskEmail(lead.email)}`)
    return { success: true, messageId: response.text }

  } catch (error) {
    log.error('Drip email send error:', error)
    return { success: false, error: error.text || error.message }
  }
}

/**
 * Update drip state in database after sending email
 */
async function updateDripState(lead, emailId, previousEmailsSent) {
  const newEmailsSent = [...previousEmailsSent, emailId]
  const isComplete = newEmailsSent.length >= DRIP_CAMPAIGN.emails.length

  const newDripState = {
    emailsSent: newEmailsSent,
    lastEmailSentAt: new Date().toISOString(),
    lastEmailId: emailId,
    completed: isComplete,
    completedAt: isComplete ? new Date().toISOString() : null
  }

  // Update based on source table
  if (lead.source === 'quote_requests') {
    const { error } = await supabase
      .from('quote_requests')
      .update({
        drip_state: newDripState,
        last_updated: new Date().toISOString()
      })
      .eq('id', lead.id)

    if (error) {
      log.error('Error updating drip state:', error)
    }
  }

  // Also log to activity/notes
  await logDripActivity(lead, emailId, newEmailsSent.length)
}

/**
 * Log drip email to lead's activity/notes AND activities timeline
 */
async function logDripActivity(lead, emailId, emailNumber) {
  const emailConfig = DRIP_CAMPAIGN.emails.find(e => e.id === emailId)
  const now = new Date().toISOString()

  if (lead.source === 'quote_requests') {
    // Get current notes AND activities
    const { data: quote } = await supabase
      .from('quote_requests')
      .select('notes, activities')
      .eq('id', lead.id)
      .maybeSingle()

    const currentNotes = quote?.notes || []
    const currentActivities = quote?.activities || []
    
    // Add to notes
    const newNote = {
      id: Date.now(),
      text: `📧 DRIP #${emailNumber}: "${emailConfig?.subject || emailId}" sent automatically`,
      timestamp: now,
      author: 'Drip Campaign'
    }

    // Add to activities timeline
    const newActivity = {
      id: Date.now() + 1, // Ensure unique ID
      type: 'drip_email_sent',
      description: `Retarget Email #${emailNumber} sent: "${emailConfig?.subject || emailId}"`,
      details: {
        emailId: emailId,
        emailNumber: emailNumber,
        subject: emailConfig?.subject,
        recipientEmail: lead.email
      },
      timestamp: now,
      user: 'Drip Campaign (Auto)'
    }

    await supabase
      .from('quote_requests')
      .update({ 
        notes: [newNote, ...currentNotes],
        activities: [newActivity, ...currentActivities],
        last_updated: now
      })
      .eq('id', lead.id)
    
    log.debug(`Activity logged for drip email #${emailNumber}`)
  }
}

// =============================================================================
// MANUAL CONTROLS
// =============================================================================

/**
 * Stop drip campaign for a specific lead (unsubscribe)
 */
export async function stopDripForLead(leadId, source = 'quote_requests') {
  const newDripState = {
    unsubscribed: true,
    unsubscribedAt: new Date().toISOString()
  }

  const { error } = await supabase
    .from(source)
    .update({
      drip_state: newDripState,
      last_updated: new Date().toISOString()
    })
    .eq('id', leadId)

  if (error) {
    log.error('Error stopping drip:', error)
    return { success: false, error }
  }

  log.info(`Drip stopped for lead ${leadId}`)
  return { success: true }
}

/**
 * Restart drip campaign for a lead
 */
export async function restartDripForLead(leadId, source = 'quote_requests') {
  const newDripState = {
    emailsSent: [],
    lastEmailSentAt: null,
    completed: false,
    unsubscribed: false,
    restartedAt: new Date().toISOString()
  }

  const { error } = await supabase
    .from(source)
    .update({
      drip_state: newDripState,
      last_updated: new Date().toISOString()
    })
    .eq('id', leadId)

  if (error) {
    log.error('Error restarting drip:', error)
    return { success: false, error }
  }

  log.info(`Drip restarted for lead ${leadId}`)
  return { success: true }
}

/**
 * Get drip status for a lead
 */
export async function getDripStatus(leadId, source = 'quote_requests') {
  const { data, error } = await supabase
    .from(source)
    .select('drip_state, last_updated')
    .eq('id', leadId)
    .maybeSingle()

  if (error) {
    return { error }
  }

  const dripState = data?.drip_state || {}
  const emailsSent = dripState.emailsSent || []

  return {
    active: !dripState.completed && !dripState.unsubscribed,
    emailsSent: emailsSent.length,
    totalEmails: DRIP_CAMPAIGN.emails.length,
    lastEmailSentAt: dripState.lastEmailSentAt,
    completed: dripState.completed || false,
    unsubscribed: dripState.unsubscribed || false,
    nextEmail: getNextEmailInfo(emailsSent)
  }
}

function getNextEmailInfo(emailsSent) {
  const nextEmail = DRIP_CAMPAIGN.emails.find(e => !emailsSent.includes(e.id))
  return nextEmail ? {
    id: nextEmail.id,
    subject: nextEmail.subject,
    dayOffset: nextEmail.dayOffset
  } : null
}

// =============================================================================
// STATUS
// =============================================================================

export function getDripWorkerStatus() {
  return {
    isRunning,
    lastRunTime,
    campaign: DRIP_CAMPAIGN.name,
    emailCount: DRIP_CAMPAIGN.emails.length
  }
}

// =============================================================================
// AUTO-START (optional - can be triggered from App.tsx)
// =============================================================================

// Uncomment to auto-start on module load:
// if (typeof window !== 'undefined') {
//   setTimeout(() => startDripWorker(), 5000)
// }

export default {
  startDripWorker,
  stopDripWorker,
  forceProcessDrip,
  stopDripForLead,
  restartDripForLead,
  getDripStatus,
  getDripWorkerStatus
}

